import React, { useState, useCallback, useMemo, useEffect } from 'react';
import '../css/Cupones.css';
import { ReactComponent as Eliminar } from '../assets/icon/eliminar.svg';
import { ReactComponent as Editar } from '../assets/icon/editar.svg';
import { ReactComponent as Agregar } from '../assets/icon/agregarCupon.svg';
import { ReactComponent as Recargar } from '../assets/icon/recargar.svg';
import { useNavigate } from "react-router-dom";
import TablaCupones from '../Cupones/TablaCupones';
import { useCupones, deleteCupon } from '../services/cupones';
import { useToast } from '../contexts/ToastContext';
import BulkDeleteModal from '../Components/BulkDeleteModal';

function Cupones() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItems, setDeletingItems] = useState([]);
  const { showError, showSuccess } = useToast();

  const { data: cupones, loading, error, refetch } = useCupones({
    immediate: true
  });

  useEffect(() => {
    if (error) {
      showError(error, 'Error de conexión');
    }
  }, [error, showError]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    
    const itemsToDelete = cupones.filter(c => selectedIds.includes(c.IdCupon));
    setDeletingItems(itemsToDelete);
    setShowDeleteModal(true);
  }, [selectedIds, cupones]);

  const handleConfirmDelete = useCallback(async (id, item) => {
    const result = await deleteCupon(id);
    if (result.success && result.error === 0) {
      return { success: true };
    }
    throw new Error(result.mensaje || 'Error al eliminar');
  }, []);

  const handleDeleteComplete = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedIds([]);
    refetch();
    showSuccess('Cupón(es) eliminado(s) correctamente');
  }, [refetch, showSuccess]);

  const handleEditSelected = useCallback(() => {
    if (selectedIds.length === 1) {
      navigate(`/dashboard/Cupones/editarcupones/${selectedIds[0]}`);
    }
  }, [selectedIds, navigate]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const buttonStates = useMemo(() => {
    const hasNoSelection = selectedIds.length === 0;
    const hasSingleSelection = selectedIds.length === 1;
    const hasMultipleSelection = selectedIds.length > 1;
    const hasData = cupones && cupones.length > 0;

    return {
      nuevo: hasNoSelection && hasData,
      editar: hasSingleSelection && hasData,
      eliminar: (hasSingleSelection || hasMultipleSelection) && hasData
    };
  }, [selectedIds, cupones]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="bodyCupones">

      {/* TÍTULO */}
      <div className='tituloCupones'><p>Cupones</p></div>

      {/* CONTENIDO SUPERIOR */}
      <div className='contenidoSuperiorCupones'>
        <div className='botonesCupones'>
          <button
            className='botonesEdicionCupones'
            onClick={() => navigate("/dashboard/Cupones/editarcupones")}
            disabled={!buttonStates.nuevo}
            title={buttonStates.nuevo ? "Agregar nuevo cupón" : "Deshabilitado cuando hay selección"}
          >
            <div className="svgBotonesCupones"><Agregar className="logo-svgBotones" /></div>
            Agregar Cupon
          </button>

          <button
            className='botonesEdicionCupones'
            disabled={!buttonStates.editar}
            onClick={handleEditSelected}
            title={buttonStates.editar ? "Editar cupón seleccionado" : "Selecciona un cupón para editar"}
          >
            <div className="svgBotonesCupones"><Editar className="logo-svgBotones" /></div>
            Editar Cupon
          </button>

          <button 
            className='botonesEdicionCupones'
            disabled={!buttonStates.eliminar}
            onClick={handleDeleteSelected}
            title={buttonStates.eliminar ? `Eliminar ${selectedIds.length} cupón(es)` : "Selecciona cupones para eliminar"}
          >
            <div className="svgBotonesCupones"><Eliminar className="logo-svgBotones" /></div>
            Eliminar Cupon{selectedIds.length > 1 ? 'es' : ''}
          </button>

          <button 
            className='botonesEdicionCupones'
            onClick={handleRefresh}
            disabled={loading}
            title="Recargar cupones"
          >
            <div className="svgBotonesCupones">
              <Recargar className={`logo-svgBotones ${loading ? 'rotating' : ''}`} />
            </div>
            Recargar
          </button>
        </div>
      </div>

      {/* AQUÍ SE MOSTRARÁ LA TABLA */}
      <div className="tablaContainerCupones">
        <TablaCupones 
          cupones={cupones}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyStateNoReload={true}
          pageSize={20}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSelectionChange={(selectedIdsList) => {
            setSelectedIds(selectedIdsList || []);
            if (currentPage !== 1) {
              setCurrentPage(1);
            }
          }}
        />
      </div>

      <BulkDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Cupones"
        items={deletingItems}
        onDelete={handleConfirmDelete}
        getItemId={(item) => item.IdCupon}
        getItemLabel={(item) => item.CodigoPromo || `Cupón #${item.IdCupon}`}
        entityName="cupón"
      />

    </div>
  );
}

export default Cupones;
